import moment, { Moment } from 'moment-timezone';
import {
  DateString,
  IMinimumGuaranteeReport,
  IntervalType,
  ISeason,
  ISegmentCurationFeaturedCardType,
  ISegmentCurationType,
  IStory,
} from '../../services/types';
import settings from '../settings';
import { keyBy } from 'lodash';
import { QueryClient, QueryKey } from 'react-query';

// TODO: seperate functions and make a index file to index
export const getIntervalValueAndPeriodText = (type: IntervalType, value: number, utcPeriod?: number): string => {
  let ret = '';

  ret = value === 1 ? `Every ${type}` : `Every ${value} ${type}s`;

  if ((type === IntervalType.Daily || type === IntervalType.Weekly || type === IntervalType.Monthly) && utcPeriod !== undefined) {
    ret += ` ${convertPeriodWithTimezone(type, utcPeriod, true)}`;
  }

  return ret;
};

export const convertPeriodWithTimezone = (type: IntervalType, period: number, needDisplayFormat = false): string => {
  let utcFormat = '',
    displayFormat = '',
    input = period;

  const curTz = settings.getTimezone();

  switch (type) {
    case IntervalType.Daily:
      (utcFormat = 'H'), (displayFormat = 'LT');
      break;

    case IntervalType.Weekly:
      (utcFormat = 'd'), (displayFormat = 'dddd');
      break;

    case IntervalType.Monthly:
      (utcFormat = 'D'), (displayFormat = 'Do'), (input = period + 1);
      break;
  }

  const format = needDisplayFormat ? displayFormat : utcFormat;

  if (type === IntervalType.Monthly) {
    return moment(input, utcFormat).format(format);
  } else {
    return moment.utc(input, utcFormat).tz(curTz).format(format);
  }
};

export const getCurrentHourFromUTCHour = (utcHour: number) => {
  const curTz = settings.getTimezone();

  const hr = moment.utc(utcHour, 'H').tz(curTz).format('H');

  return Number(hr);
};

export const replace = <T extends any>(arr: T[], predicate: (value: T) => boolean, replacement: T) => {
  const cloned = arr && arr.length ? [...arr] : [];
  const toBeReplacedIndex = cloned.findIndex(predicate);
  if (toBeReplacedIndex >= 0) {
    cloned.splice(toBeReplacedIndex, 1, replacement);
  } else {
    cloned.push(replacement);
  }

  return cloned;
};

export const merge = <T extends any>(source: T[], target: T[], key: string) => {
  const mergedMap = Object.assign({}, keyBy(source, key), keyBy(target, key));

  return Object.keys(mergedMap).map(k => mergedMap[k]);
};

export const getPathnameFromURI = (uri: string, retainHeadingSlash = false) => {
  const regex = /(http[s]?:\/\/)/;

  let pathname = uri;

  if (regex.test(uri)) {
    try {
      const u = new URL(uri);
      pathname = u.pathname;
    } catch (e) {}
  }

  const hasHeadingSlash = pathname?.substr(0, 1) === '/';

  if (hasHeadingSlash && retainHeadingSlash == false) {
    // trim
    pathname = pathname.substr(1);
  }

  return pathname;
};

export const getMatchedSeason = (story: IStory): ISeason | undefined => {
  return story.seasonId ? story.seasons?.filter(s => s.id === story.seasonId).pop() : story.firstSeason || story.seasons?.[0];
};

export const getNextFireDate = (
  intervalType: IntervalType,
  intervalValue: number,
  period?: number,
  utcHourOnWeeksAndMonths = 0,
  startDate?: DateString,
): Moment | undefined => {
  let nextAt: Moment | undefined = undefined;

  const tz = settings.getTimezone();

  const addIntervalValue = (m: Moment, unit: 'months' | 'weeks' | 'days') => {
    if (m.isSameOrAfter(moment())) {
      m.add(intervalValue - 1, unit);
    } else {
      m.add(1, unit);
    }
  };

  switch (intervalType) {
    case IntervalType.Monthly:
      nextAt = moment(startDate).utc().startOf('month').add(period!, 'days').hour(utcHourOnWeeksAndMonths).tz(tz);

      addIntervalValue(nextAt, 'months');
      break;

    case IntervalType.Weekly:
      nextAt = moment(startDate).utc().startOf('week').add(period, 'days').hour(utcHourOnWeeksAndMonths).tz(tz);

      addIntervalValue(nextAt, 'weeks');

      break;

    case IntervalType.Daily:
      nextAt = moment(startDate).startOf('day').add(period, 'hours');

      addIntervalValue(nextAt, 'days');
      break;

    case IntervalType.Hourly:
      nextAt = moment(startDate).startOf('hour').add(intervalValue, 'hours');
      break;

    case IntervalType.Minutely:
      nextAt = moment(startDate).startOf('minute').add(intervalValue, 'minutes');
      break;

    default:
      break;
  }

  return nextAt;
};

export const getCoverUrl = (story: IStory, type?: 'h' | 'v'): string | null => {
  if (story.seasons && story.seasons.length > 0) {
    const season = story.seasons[0];
    if (type === 'h' && season.additionalCoverHUrl) {
      return season.additionalCoverHUrl;
    }
    if (type === 'v' && season.additionalCoverVUrl) {
      return season.additionalCoverVUrl;
    }
    return season.coverUrl || null;
  }
  return null;
};

export const getStatusByDates = (start: Date | null | undefined, end: Date | null | undefined): string => {
  const now = moment();
  if (end !== null && now.isSameOrAfter(end)) {
    return 'completed';
  } else if (now.isBefore(start)) {
    return 'scheduled';
  } else {
    return 'activated';
  }
};

export const invalidateQueriesWithDelay = (queryClient: QueryClient, queryKey: QueryKey, delay = 500): NodeJS.Timeout =>
  setTimeout(() => queryClient.invalidateQueries(queryKey), delay);

export const downloadCSVFromText = (text: string, filename: string): void => {
  const a = document.createElement('a');
  a.download = `${filename}.csv`;
  a.href = `data:text/csv;charset=utf-8,${encodeURIComponent(text)}`;
  a.click();
};

export const convertFirstCharacterToUppercase = (str: string): string => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const isFeaturedCard = (type: ISegmentCurationType | undefined): type is ISegmentCurationFeaturedCardType =>
  type === ISegmentCurationType.AudioFeatured || type === ISegmentCurationType.FeaturedCards || type === ISegmentCurationType.TextFeatured;

export const isPositiveNumber = (value: string | number | undefined): boolean => {
  if (typeof value === 'string') {
    return /^\d+\.?\d*$/.test(value);
  } else if (typeof value === 'number') {
    return value >= 0;
  } else {
    return false;
  }
};

export const numberToPercentage = (value: string | number | undefined): string => {
  if (value === undefined) return '';

  return `${Number(value)}%`;
};

export const numberToCurrency = (value: string | number | undefined, locale = 'en-US', maximumFractionDigits = 5): string => {
  if (value === undefined) return '';

  return `$${value.toLocaleString(locale, {
    maximumFractionDigits,
  })}`;
};

export const getMGContractType = (monthlyReport: IMinimumGuaranteeReport) => {
  const { writer, story, season } = monthlyReport.contract;
  if (writer) {
    return {
      contractType: "Writer's Account",
      storyTitle: '-',
      seasonTitle: '-',
      writerName: writer.ownedPennames?.sort((a, b) => moment(a.createdAt).diff(b.createdAt))[0]?.name || '',
      writerId: writer.ownedPennames?.sort((a, b) => moment(a.createdAt).diff(b.createdAt))[0]?.id || undefined,
      storyId: undefined,
      seasonId: undefined,
    };
  } else if (story) {
    return {
      contractType: 'Story',
      storyTitle: story.title,
      seasonTitle: 'All Seasons',
      writerName: story.penname.name,
      writerId: story.penname.id,
      storyId: story.id,
      seasonId: undefined,
    };
  } else if (season) {
    return {
      contractType: 'Season',
      storyTitle: season.story.title,
      seasonTitle: season.title || `Season ${season.no}`,
      writerName: season.story.penname.name,
      writerId: season.story.penname.id,
      storyId: season.storyId,
      seasonId: season.id,
    };
  } else {
    return { contractType: '', storyTitle: '', seasonTitle: '', writerName: '', writerId: undefined, storyId: undefined, seasonId: undefined };
  }
};
